import { RouteLocation, Router, RouteLocationNormalized } from "vue-router";
import IExtendedSearchQueryFilter from "../interfaces/dtos/IExtendedSearchQueryFilter";
import type { RouteMatchResult } from "@domain/interfaces/mappers/RouteMatchResult";
import { FilterType } from "@studyportals/omnisearch-interfaces";
import { FilterMapper } from "@app/routing/mappers/FilterMapper";
import { IExtendedFilterValue } from "@app/interfaces/dtos/IExtendedFilterValue";
import { PortalType } from "@studyportals/domain-client";
import { getPortalTypeFromEnvClient } from "@app/utils/get-portal-type-from-env.client";
import { getPortalTypeFromEnvServer } from "@app/utils/get-portal-type-from-env.server";
import { getPortalPathFromType } from "@app/utils/portal-detector";
import { availableFilterMappers, getAllowedFilterTypesForSearchType } from "@domain/mappers/AvailableFilterMappers";
import { SearchType } from "@domain/enums/SearchType";
import { ROUTE_PREFIX } from "@generator/config/route-prefix";



const filterTypes = Object.values(FilterType) as string[];
export class FilterProcessingService {
	private _mappers: FilterMapper[] = [];
	private static instance: FilterProcessingService;

	public static getInstance(): FilterProcessingService {
		if (!FilterProcessingService.instance) {
			FilterProcessingService.instance = new FilterProcessingService();
		}
		return FilterProcessingService.instance;
	}

	private constructor() {
		this._mappers = [
			...availableFilterMappers
		];
	}

	public get mappers(): FilterMapper[] {
		return this._mappers
	}


	public async processRoute(route: RouteLocation): Promise<IExtendedSearchQueryFilter[]> {
		const filters: IExtendedSearchQueryFilter[] = [];

		for (const mapper of this.mappers) {
			const values = await mapper.getExtendedFilterValuesFromRoute(route);
			if (values.length > 0) {
				filters.push({
					type: mapper.filterType,
					isIndexable: values.length === 1 && mapper.isIndexable(values[0]!.id),
					values
				})
			}
		}

		return this.orderFiltersByRouteParams(filters, route);
	}

	public async setSearchTypeRedirection(router: Router, targetSearchType: SearchType): Promise<RouteMatchResult> {
		const currentPortalType = import.meta.client
			? getPortalTypeFromEnvClient()
			: getPortalTypeFromEnvServer();
		const routePortalPath = getPortalPathFromType(
			currentPortalType,
		);
		const prefix = router.currentRoute.value.path.startsWith(ROUTE_PREFIX)
			? ROUTE_PREFIX
			: "";
		const searchPath = targetSearchType === SearchType.UNIVERSITY_SEARCH ?
			`${prefix}/search/universities/${routePortalPath}` : `${prefix}/search/${routePortalPath}`;

		const filters = await this.processRoute(
			router.currentRoute.value,
		);
		const routeMatch = this.processFilters(
			router,
			filters,
			{
				...router.currentRoute.value,
				path: searchPath,
				meta: {
					...router.currentRoute.value.meta,
					searchType: targetSearchType,
				},
			},
		);
		return routeMatch;
	}

	public ensureImplicitFilters(filters: IExtendedSearchQueryFilter[], portalType: PortalType): IExtendedSearchQueryFilter[] {
		let result = [...filters];

		for (const mapper of this.mappers) {
			const implicitValues = mapper.getImplicitFilterValues(portalType);
			if (implicitValues.length === 0) continue;

			const existingFilter = result.find(f => f.type === mapper.filterType);

			if (existingFilter) {
				const existingIds = new Set(existingFilter.values.map(v => v.id));
				const newValues = implicitValues.filter(v => !existingIds.has(v.id));

				if (newValues.length > 0) {
					result = result.map(f =>
						f.type === mapper.filterType
							? { ...f, values: [...f.values, ...newValues] }
							: f
					);
				}
			} else {
				result.push({
					type: mapper.filterType,
					values: implicitValues,
				});
			}
		}

		return result;
	}

	public processFilters(router: Router, filters: IExtendedSearchQueryFilter[], routeLocation?: RouteLocationNormalized): RouteMatchResult {
		const params: Partial<Record<FilterType, string>> = {};
		const query: Partial<Record<string, string>> = {};
		const currentPortalType = import.meta.client ? getPortalTypeFromEnvClient() : getPortalTypeFromEnvServer();
		const isDistanceLearningPortal = currentPortalType === PortalType.DISTANCE_LEARNING;
		const to: RouteLocationNormalized = routeLocation ?? router.currentRoute.value;
		const desiredSearchType = this.getSearchType(to);
		const allowedFilterTypes = getAllowedFilterTypesForSearchType(desiredSearchType);

		const allRoutes = router.getRoutes();
		const baseRoutes = allRoutes
			.filter(r => !r.name?.toString().endsWith('OmniSearch'))
			.filter(r => this.matchesSearchType(r.meta?.searchType as SearchType | undefined, desiredSearchType));
		const hasDisallowedDeclaredFilters = (route?: RouteLocationNormalized | { meta?: { filterTypes?: FilterType[] } }): boolean => {
			const declaredFilterTypes = route?.meta?.filterTypes ?? [];
			return declaredFilterTypes.some((type) => !allowedFilterTypes.has(type));
		};
		const getEffectiveRouteFilterTypes = (route?: RouteLocationNormalized | { meta?: { filterTypes?: FilterType[] } }): FilterType[] => {
			const declaredFilterTypes = (route?.meta?.filterTypes ?? []).filter(type => allowedFilterTypes.has(type));
			if (!isDistanceLearningPortal) return declaredFilterTypes;

			return declaredFilterTypes.filter(type => type !== FilterType.EDUCATION_LEVEL);
		};

		const eligibleFilterTypesForPath = filters
			.filter(filter => allowedFilterTypes.has(filter.type))
			.filter(filter => !isDistanceLearningPortal || filter.type !== FilterType.EDUCATION_LEVEL)
			.filter(filter => filter.values.length === 1 && filter.values[0]!.virtualPath)
			.map(filter => filter.type);
		const rootRouteNameBySearchType: Record<SearchType, string> = {
			[SearchType.PROGRAMME_SEARCH]: 'Portal',
			[SearchType.UNIVERSITY_SEARCH]: 'PortalUniversity',
			[SearchType.SCHOLARSHIP_SEARCH]: 'PortalScholarship',
		};
		let selectedRoute = baseRoutes.find(route => route.name === rootRouteNameBySearchType[desiredSearchType])
			?? allRoutes.find(route => route.name === 'Portal');

		for(const route of baseRoutes) {
			if(!route.meta.filterTypes) continue;
			if (hasDisallowedDeclaredFilters(route)) continue;

			const effectiveCandidateRouteFilterTypes = getEffectiveRouteFilterTypes(route);
			const selectedRouteFilterTypes = getEffectiveRouteFilterTypes(selectedRoute);

			if(selectedRouteFilterTypes && effectiveCandidateRouteFilterTypes.length <= selectedRouteFilterTypes.length) continue;
			if(effectiveCandidateRouteFilterTypes.length > eligibleFilterTypesForPath.length) continue;

			const includesAllFilters = effectiveCandidateRouteFilterTypes.every(type => eligibleFilterTypesForPath.includes(type));
			if(includesAllFilters) selectedRoute = route;
		}

		if (!selectedRoute) {
			throw new Error('No route candidates were found for the selected search type');
		}

		// Preserve existing non-filter query params from the current route. Drop pagination; preserve others (e.g. utm_*).
		const nonFilterQueryKeys = Object.keys(to?.query || {}).filter(
			q => !filterTypes.includes(q) && q !== 'page' && q !== 'mh'
		);

		for (const key of nonFilterQueryKeys) {
			query[key] = to.query[key] as string;
		}

		const routeFilterTypes = getEffectiveRouteFilterTypes(selectedRoute);
		for(const filter of filters) {
			if (!allowedFilterTypes.has(filter.type)) {
				query[filter.type] = filter.values.map(v => v.id).join(',');
				continue;
			}

			if(routeFilterTypes.includes(filter.type)) {
				if (filter.values[0]!.virtualPath) {
					params[filter.type] = filter.values[0]!.virtualPath;
					continue;
				}

				query[filter.type] = filter.values.map(v => v.id).join(',');
				continue;
			}

			query[filter.type] = filter.values.map(v => v.id).join(',');
		}

		if (isDistanceLearningPortal) {
			params[FilterType.EDUCATION_LEVEL] = getPortalPathFromType(currentPortalType);
		}

		const baseName = selectedRoute.name!.toString();
		const isOmniSearchOrigin = to?.path?.includes('/omni-search/');
		const routeName = isOmniSearchOrigin && allRoutes.some(r => r.name === `${baseName}OmniSearch`)
			? `${baseName}OmniSearch`
			: baseName;

		return {
			name: routeName,
			params,
			query: query as Record<string, string>,
			hash: to.hash
		};
	}

	private matchesSearchType(searchType: SearchType | undefined, desiredSearchType: SearchType): boolean {
		if (searchType === undefined) {
			return desiredSearchType === SearchType.PROGRAMME_SEARCH;
		}

		return searchType === desiredSearchType;
	}

	public getSearchType(route?: Pick<RouteLocation, 'path' | 'meta'>): SearchType {
		const metaSearchType = (route?.meta as { searchType?: unknown } | undefined)?.searchType;
		if (metaSearchType === SearchType.UNIVERSITY_SEARCH || metaSearchType === SearchType.SCHOLARSHIP_SEARCH || metaSearchType === SearchType.PROGRAMME_SEARCH) {
			return metaSearchType;
		}

		return SearchType.PROGRAMME_SEARCH;
	}

	public translateValuesToExtendedFilters(filterType: FilterType, values: string[]): IExtendedFilterValue[] {
		const mapper = this.mappers.find(m => m.filterType === filterType);
		if (!mapper) return [];

		return values
			.map(value => mapper.getExtendedFilterValueById(value))
			.filter(value => value !== undefined);
	}

	private orderFiltersByRouteParams(filters: IExtendedSearchQueryFilter[], route: RouteLocation): IExtendedSearchQueryFilter[] {
		const routeParamKeys = Object.keys(route.params);

		return filters.sort((a, b) => {
			const aIndex = routeParamKeys.indexOf(a.type);
			const bIndex = routeParamKeys.indexOf(b.type);

			if (aIndex === -1 && bIndex === -1) return 0;
			if (aIndex === -1) return 1;
			if (bIndex === -1) return -1;
			return aIndex - bIndex;
		});
	}

}
